import { api } from "@/auth/api";
import { settings } from "@/settings";
import { saveProposalItem } from "@/store/services/configuration/AssetService";
import { UON } from "@/utils";

// Values typed by the user on an order (not picked from a list) are saved in the reference tables,
// so they are offered in the lists of the next orders:
// - options / accessories / fees -> odm-product goods (same as the Goods configuration screen, saveProposalItem)
// - service types -> odm-order public codes of OrderAssetServiceType (internal code CUSTOM, typed text as code)
// Everything is collected before the save, since the save and the reload drop the "typed-in" flags.

const orderBasePath = `${settings.api_url}/${UON.SystemUid.odmOrder}/api/1/${UON.SystemUid.odmOrder}`;

export interface NewSupplement {
  type: string;
  label: string;
  priceWoTax: number;
  taxCode: string;
}

export interface NewServiceType {
  code: string;
  label: string;
}

// Order component type -> type expected by saveProposalItem (anything else is a fee: NATURE.ASSETFEE)
const SUPPLEMENT_TYPES: Record<string, string> = {
  OPTION: "goodOption",
  ACCESSORY: "goodAccessory",
  OTHERS: "goodFee",
};

const orderAssets = (orders: any[]) =>
  (orders || []).flatMap((order: any) => order?.orderAssets || []);

/**
 * Lists the typed-in options, accessories and fees of the orders (one per type and label).
 */
export function collectNewSupplements(orders: any[]): NewSupplement[] {
  const result: NewSupplement[] = [];
  const seen = new Set<string>();
  for (const component of orderAssets(orders).flatMap((asset: any) => asset?.orderAssetComponents || [])) {
    const type = component?.type?.resourceUid || component?.type;
    const label = (component?.label || "").trim();
    if (!component?.config?.freeValue || !label || !SUPPLEMENT_TYPES[type]) continue;
    const key = `${type}|${label.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      type,
      label,
      priceWoTax: Number(component.amountWoTax?.amount ?? component.config.unitaryAmountModel) || 0,
      taxCode: component.config.taxValueModel?.code || "",
    });
  }
  return result;
}

/**
 * Lists the typed-in service types of the orders (one per code).
 */
export function collectNewServiceTypes(orders: any[]): NewServiceType[] {
  const result: NewServiceType[] = [];
  for (const service of orderAssets(orders).flatMap((asset: any) => asset?.orderAssetServices || [])) {
    const serviceType = service?.type?.type;
    if (!serviceType?.freeValue || !serviceType.value || serviceType.value === "CUSTOM") continue;
    if (result.some((item) => item.code === serviceType.value)) continue;
    result.push({ code: serviceType.value, label: serviceType.label });
  }
  return result;
}

/**
 * Services whose type could not be created fall back to the CUSTOM type (their label is kept),
 * so the order can still be saved.
 */
export function useCustomServiceType(orders: any[], failedCodes: string[]) {
  if (failedCodes.length === 0) return;
  for (const service of orderAssets(orders).flatMap((asset: any) => asset?.orderAssetServices || [])) {
    if (service?.type?.type?.freeValue && failedCodes.includes(service.type.resourceUid)) {
      service.type.resourceUid = "CUSTOM";
      service.type.type = { ...service.type.type, value: "CUSTOM" };
    }
  }
}

/**
 * Creates the options / accessories / fees in odm-product. Returns the labels that could not be created.
 */
export async function saveNewSupplements(supplements: NewSupplement[]): Promise<string[]> {
  const failed: string[] = [];
  for (const supplement of supplements) {
    try {
      await saveProposalItem(
        {
          nameItem: supplement.label,
          required: false,
          priceWoTax: supplement.priceWoTax,
          typeTax: { value: supplement.taxCode },
          status: "USE",
          advancedMode: false,
        },
        SUPPLEMENT_TYPES[supplement.type]
      );
    } catch (err) {
      console.error(err);
      failed.push(supplement.label);
    }
  }
  return failed;
}

/**
 * Creates the service types in odm-order. Returns the codes that could not be created.
 */
export async function saveNewServiceTypes(serviceTypes: NewServiceType[]): Promise<string[]> {
  const failed: string[] = [];
  for (const serviceType of serviceTypes) {
    try {
      await api().post(`${orderBasePath}/orderassetservicetype/`, {
        resourceUid: serviceType.code,
        objectType: "odm.order.orderassetservicetype",
        systemUid: UON.SystemUid.odmOrder,
        internalCode: "CUSTOM",
        label: serviceType.label,
        defaultValue: false,
      });
    } catch (err) {
      console.error(err);
      failed.push(serviceType.code);
    }
  }
  return failed;
}

